import { useCallback, useEffect, useState } from 'react';
import { TFile } from 'types/user';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useNavigate } from 'react-router-dom';
import {
  createPreSeizure,
  createSeizure,
  getFiles,
  updateDetailPostSeizure,
  updateDetailPreSeizure
} from 'services/files';
import { createFileNote, CreateFileNotePayload } from 'services/fileNote';
import dayjs from 'dayjs';
import { useFlash } from 'contexts/flash';

const CATEGORY = {
  REDEMPTION: 'redemption',
  POST_SEIZURE: 'post-seizure',
  PRE_SEIZURE: 'pre-seizure'
};
const size = 50;
export const useRedemption = () => {
  const { setFlash } = useFlash();

  const navigate = useNavigate();
  const [rowsPost, setRowsPost] = useState<TFile[]>([]);
  const [rowsPre, setRowsPre] = useState<TFile[]>([]);
  const [totalPost, setTotalPost] = useState<number>(0);
  const [totalPre, setTotalPre] = useState<number>(0);
  const [pagePost, setPagePost] = useState(1);
  const [pagePre, setPagePre] = useState(1);

  const fetchDataPostSeizure = useCallback(async ({ page }: { page: number }) => {
    const data = await getFiles({
      fileStatus: CATEGORY.POST_SEIZURE,
      page: page,
      size
    });
    setRowsPost(data.files);
    setTotalPost(data.total);
  }, []);

  const fetchDataPreSeizure = useCallback(async ({ page }: { page: number }) => {
    const data = await getFiles({
      fileStatus: CATEGORY.PRE_SEIZURE,
      page: page,
      size
    });
    setRowsPre(data.files);
    setTotalPre(data.total);
  }, []);

  const { asyncCallback: asyncFetchPost } = useAsyncCallback(fetchDataPostSeizure, []);
  const { asyncCallback: asyncFetchPre } = useAsyncCallback(fetchDataPreSeizure, []);

  useEffect(() => {
    asyncFetchPost({ page: pagePost });
  }, [pagePost]);

  useEffect(() => {
    asyncFetchPre({ page: pagePre });
  }, [pagePre]);

  const onChangePagePost = useCallback((page: number | string) => {
    setPagePost(Number(page));
  }, []);

  const onChangePagePre = useCallback((page: number | string) => {
    setPagePre(Number(page));
  }, []);

  const handleClickRow = useCallback(
    (id: string, isPostSeizure: boolean, fileId: number) => {
      if (isPostSeizure) {
        if (!id) {
          return navigate(`/admin/redemptions/post-seizure/create?fileId=${fileId}`);
        }
        return navigate(`/admin/redemptions/post-seizure/edit/${id}`);
      }
      if (!id) {
        return navigate(`/admin/redemptions/pre-seizure/create?fileId=${fileId}`);
      }
      return navigate(`/admin/redemptions/pre-seizure/edit/${id}`);
    },
    [navigate]
  );

  const updateRedemption = async ({
    status,
    fileId,
    apptDate,
    documentIds,
    id,
    followUpSent,
    statementSent
  }: {
    fileId: number;
    status: string;
    id: string;
    apptDate?: string;
    followUpSent?: string;
    statementSent?: string;
    documentIds?: number[];
  }) => {
    const notes: string[] = [];

    if (statementSent) {
      notes.push(`Updated Statement Sent date to ${dayjs(statementSent).format('YYYY/MM/DD')}`);
    }

    if (followUpSent) {
      notes.push(`Updated Followup Sent date to ${dayjs(followUpSent).format('YYYY/MM/DD')}`);
    }

    if (apptDate) {
      notes.push(
        `Updated Appointment date to ${dayjs(apptDate).format(status === 'POST_SEIZURE' ? 'YYYY/MM/DD HH:mm A' : 'YYYY/MM/DD')}`
      );
    }
    if (notes.length) {
      const fileNote: CreateFileNotePayload = {
        fileId: Number(fileId),
        content: notes.join(' | ')
      };
      await createFileNote(fileNote);
    }
    const payload = {
      apptDate,
      fileId,
      documentIds,
      followUpSent,
      statementSent
    };
    if (status === 'POST_SEIZURE') {
      if (!id) {
        await createSeizure(payload);
      } else {
        await updateDetailPostSeizure(Number(id), payload);
      }
    } else {
      if (!id) {
        await createPreSeizure(payload);
      } else {
        await updateDetailPreSeizure(Number(id), payload);
      }
    }
    setFlash({ type: 'success', message: 'Updated successful' });
  };

  const { asyncCallback: asyncUpdateRedemption } = useAsyncCallback(updateRedemption, []);

  return {
    size,
    rowsPost,
    rowsPre,
    totalPost,
    totalPre,
    pagePost,
    pagePre,
    onChangePagePost,
    onChangePagePre,
    handleClickRow,
    asyncUpdateRedemption
  };
};
